import { Body, IconButton, Tooltip } from '@assembly-js/design-system';
import { Menu, MenuItem } from '@mui/material';
import clsx from 'clsx';
import { useState } from 'react';

import { Action } from 'src/constants';

const MenuItemContent = ({
  action,
  onClose,
}: {
  action: Action;
  onClose: () => void;
}) => {
  const { name, onClick, icon, isDelete, isDisabled, classes } = action;

  const handleClick = (e: React.MouseEvent<HTMLLIElement>) => {
    if (isDisabled) return;
    onClick(e);
    onClose();
  };

  const menuItem = (
    <MenuItem
      aria-disabled={isDisabled}
      data-testid={`action-${name}`}
      onClick={handleClick}
      disabled={isDisabled && !action.tooltipLabel}
      className={clsx(
        'flex items-center gap-4',
        {
          'text-black-heading opacity-50 cursor-not-allowed': isDisabled,
          'text-red-primary hover:!text-red-primary': isDelete && !isDisabled,
        },
        classes,
      )}
    >
      {icon}
      <Body size="sm">{name}</Body>
    </MenuItem>
  );

  if (isDisabled && action.tooltipLabel) {
    return (
      <Tooltip
        position="left"
        content={
          <Body
            size="sm"
            className="block max-w-[280px] whitespace-normal text-black-heading"
          >
            {action.tooltipLabel}
          </Body>
        }
      >
        <div className="w-full">{menuItem}</div>
      </Tooltip>
    );
  }

  return menuItem;
};

export const MenuAction = ({ actions }: { actions: Action[] }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <div>
      <IconButton
        variant="minimal"
        icon="Ellipsis"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className="hover:bg-[#F1F3F8] text-gray-small p-1 [&_svg]:w-5 [&_svg]:h-5"
      />
      <Menu
        classes={{
          paper: 'min-w-[160px]',
        }}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {actions.map((action) => (
          <MenuItemContent
            key={action.key ?? action.name}
            action={action}
            onClose={() => setAnchorEl(null)}
          />
        ))}
      </Menu>
    </div>
  );
};
